import { useEffect, useMemo, useState } from 'react';
import { FinanceiroService } from './Service';
import { formatMoneyBRL, toDateInputValue } from './utils';
import { MissingCostWarning, PendingPostingsWarning } from './FinanceWarning';

const service = new FinanceiroService();

export default function DashboardFinanceiro() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboard, setDashboard] = useState<any>(null);
    const [previsao, setPrevisao] = useState<any>(null);

    const defaultTo = useMemo(() => new Date(), []);
    const defaultFrom = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    }, []);

    const [from, setFrom] = useState<string>(toDateInputValue(defaultFrom));
    const [to, setTo] = useState<string>(toDateInputValue(defaultTo));

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await service.dashboard({ from, to });
            setDashboard(res?.data || null);
            service
                .previsaoAssinaturas()
                .then((r) => setPrevisao(r?.data || null))
                .catch((e: any) => console.error(e));
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao carregar financeiro'));
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totals = dashboard?.totals || {};

    return (
        <div className="flex flex-col gap-4">
            <div className="card bg-base-100 shadow">
                <div className="card-body py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">De</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Até</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </div>

                        <button className="btn btn-sm btn-primary md:ml-auto" onClick={load}>
                            Atualizar
                        </button>
                    </div>

                    {error && (
                        <div className="alert alert-error mt-3">
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>

            {!loading && <MissingCostWarning missingCost={dashboard?.warnings?.missingCost} />}
            {!loading && <PendingPostingsWarning count={dashboard?.warnings?.pendingPostings} />}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Receita de serviços</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.revenueServices || 0)}
                        </div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Receita de assinaturas</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.revenueSubscriptions || 0)}
                        </div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Receita de produtos</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.revenueProducts || 0)}
                        </div>
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Custo variável</div>
                        <div className="text-2xl font-semibold">
                            {loading
                                ? '...'
                                : formatMoneyBRL((totals.cogsConsumables || 0) + (totals.cogsResale || 0))}
                        </div>
                        {!loading && (
                            <div className="text-xs opacity-70">
                                Insumos: {formatMoneyBRL(totals.cogsConsumables || 0)} | Revenda:{' '}
                                {formatMoneyBRL(totals.cogsResale || 0)}
                            </div>
                        )}
                    </div>
                </div>
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="text-sm opacity-70">Lucro bruto estimado</div>
                        <div className="text-2xl font-semibold">
                            {loading ? '...' : formatMoneyBRL(totals.grossProfitEstimated || 0)}
                        </div>
                        {!loading && (
                            <div className="text-xs opacity-70">
                                Margem bruta:{' '}
                                {totals.grossMarginEstimated === null ||
                                totals.grossMarginEstimated === undefined
                                    ? '-'
                                    : `${Math.round(Number(totals.grossMarginEstimated) * 100)}%`}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <h2 className="font-medium">Previsão de assinaturas</h2>
                    <p className="text-xs opacity-70">
                        Parcelas ainda não pagas e meses futuros projetados, pelo mês de vencimento, nos próximos 12 meses. Só entram no caixa e no DRE quando forem pagas.
                    </p>
                    {previsao?.months?.length ? (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Mês</th>
                                        <th>Parcelas</th>
                                        <th className="text-right">Previsto</th>
                                        <th className="text-right">Vencido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previsao.months.map((row: any) => (
                                        <tr key={row.month}>
                                            <td>{row.month.split('-').reverse().join('/')}</td>
                                            <td>{row.quantidade}</td>
                                            <td className="text-right">{formatMoneyBRL(row.previsto)}</td>
                                            <td className="text-right">{formatMoneyBRL(row.vencido)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="font-semibold">
                                        <td colSpan={2}>Total</td>
                                        <td className="text-right">{formatMoneyBRL(previsao.total || 0)}</td>
                                        <td className="text-right">{formatMoneyBRL(previsao.totalVencido || 0)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm opacity-70">Nenhuma assinatura pendente no período.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

